
import { ReactElement, useRef, useState, useEffect} from 'react';
import { useFormContext } from 'react-hook-form';
import { getLabelFallback, onKeyPressAlpha } from '@utils/utils';

import ArrowUp from '@images/svg-icons/arrow_up.svg';
import ArrowDown from '@images/svg-icons/arrow_down.svg';
import Error from '@images/svg-icons/error.svg';
import Success from '@images/svg-icons/success.svg';

type CustomSelect = {
    options: any;
    label?: string;
    topLabel?: string;
    id: string;
    inputId: string;
    placeholder: string;
    onChange: () => void;
    defaultValue: null | string;
    showIcon?: true | false;
    controlValue?: () => void;
    clearErrorProps?: () => void;
    errorProps?: any
}

const CustomSelect = (props) : ReactElement => {

    const { formState: {errors}, clearErrors } = useFormContext();
    
    const {
        id,
        options,
        label,
        topLabel,
        placeholder,
        onChange,
        defaultValue,
        inputId,
        showIcon = true,
        controlValue,
        clearErrorProps,
        errorProps
    } = props;

    const ref = useRef(null);

    const [value, setValue] = useState(defaultValue);
    const [showItems, setShowItems] = useState(false);
    const [fromDefault, setFromDefault] = useState(false);
    
    const getItems = () => {
        return options.map((item, index) => {
            return(
                <li key={index} className={`custom-select-items-list-item ${value === item ? 'selected' : ''}`} onClick={() => handleClick(item)} onKeyDown={(e)=> console.log(e.key)}>{item.label}</li>
            )
        })
    };

    const handleClick = (item) => {
        if(!item.disabled) {
            setValue(item.code);
            setFromDefault(false);
            onChange(item.code);
            clearErrors(inputId);
            !!clearErrorProps && clearErrorProps();
            controlValue && controlValue();
        }        
    }

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (ref.current && !ref.current.contains(event.target)) {
              setShowItems(false);
            }
          };

          const handleKeyDown = (e) => {
              if (ref.current && ref.current.classList.contains('focused')) {
                const code = e.keyCode;
                
                if (!(code > 64 && code < 91) && // upper alpha (A-Z)
                    !(code > 96 && code < 123)) { // lower alpha (a-z)
                    e.preventDefault();
                } else {
                    const items = Array.prototype.slice.call(document.getElementsByClassName('custom-select-items-list-item') );
                    const index = items.findIndex(item => item.innerText.substring(0,1).toLowerCase() === e.key.toLowerCase());
                    !!document.getElementsByClassName('custom-select-items-list-item')[index] && document.getElementsByClassName('custom-select-items-list-item')[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'end' });
                }
              }           
          }

          document.addEventListener('keydown', handleKeyDown, true);
          document.addEventListener('click', handleClickOutside, true);
          return () => {
            document.removeEventListener('click', handleClickOutside, true);
          };
    }, []);

    useEffect(() => {
        setValue(defaultValue);
        setFromDefault(true);
    }, [defaultValue]);

    return(
        <button ref={ref} className={`custom-select ${showItems ? 'focused' : ''} ${(!!value && value.length > 0 && !showItems) ? 'valid' : ''} ${!!errors[inputId] || !!errorProps ? 'not-valid' : ''}`} onClick={(e) => {e.preventDefault(); e.stopPropagation(); setShowItems(showItems => !showItems)}}>
            {topLabel && !!value && <label className="custom-select-top-label">{topLabel}</label>}
            <div className="custom-select-content">
                <label>{!!value ? value : placeholder}</label>
                <div className="custom-select-content-icon">
                    {showItems ? <ArrowUp /> : <ArrowDown />}
                </div>
            </div>
            {showItems && 
                <div className="custom-select-items">
                    <ul className="custom-select-items-list">
                        {getItems()}
                    </ul>
                </div>
            }
            <label className="custom-select-label">{label}</label>
            {showIcon &&
                <div className="custom-select-icons">
                    <div className="custom-select-icon">{errors[inputId] || !!errorProps ? <Error /> : !showItems && (!fromDefault || fromDefault && !!value && value.length > 0) ? <Success /> : ''}</div>
                </div>
            }
            {(errors[inputId]) && <span className="input-error">{errors[inputId].message || getLabelFallback('FIELD_REQUIRED_LABEL').replace('{FIELD_NAME}', placeholder.toLocaleLowerCase())}</span>}
            {(errorProps) && <span className="input-error">{errorProps.message || getLabelFallback('FIELD_REQUIRED_LABEL').replace('{FIELD_NAME}', placeholder.toLocaleLowerCase())}</span>}
        </button>
    )
}

export default CustomSelect;